import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowsClockwiseIcon, CaretRightIcon, PlayIcon, StopIcon } from '@phosphor-icons/react';
import { mutate as globalMutate } from 'swr';

import { portsKey } from '@/api-models/ports';
import { restartDevServer, startDevServer, stopDevServer, useDevServerStatus } from '@/api-models/dev-server';
import { Button } from '@/ui/button/Button';
import { ApiError } from '@/utils/fetch.client';
import { Spinner } from '@/ui/Spinner';
import classNames from '@/utils/classnames';

import { DevServerLogs } from './DevServerLogs';

/**
 * The dev server's state, above the preview frame.
 *
 * It lives here rather than in a tab of its own for two reasons. A conditionally-mounted tab would
 * hide its own status until clicked, which defeats the point; an always-mounted one would poll
 * `ensure_session_sandbox_live` forever and pin the sandbox awake. And the Ports tab cannot express
 * this at all — a server that failed to compile is listening on nothing.
 *
 * It also replaces what used to be a dead end: "start a dev server in the sandbox and it will show
 * up here" told the user to go somewhere else to do the one thing this tab is for.
 */
export function DevServerStrip({ base }: { base: string }) {
  const { status, isLoading, mutate } = useDevServerStatus(base, true);
  const [busy, setBusy] = useState<null | 'start' | 'stop' | 'restart'>(null);
  const [logsOpen, setLogsOpen] = useState(false);

  const running = status?.state === 'ready';
  // The server is up and compiling. The poll below is what resolves it — the Start request itself
  // returns in a few seconds either way, so this state is normal rather than a stuck request.
  const starting = status?.state === 'starting';
  const failed = status?.state === 'failed';
  const external = status?.adopted === 'external';

  // A failure now arrives on the status poll rather than as the Start call's response — the call
  // returns `starting` long before a compile error appears — so the log has to be opened by the
  // state change, not by a rejected promise.
  useEffect(() => {
    if (failed) setLogsOpen(true);
  }, [failed]);

  async function run(action: 'start' | 'stop' | 'restart') {
    setBusy(action);
    try {
      const next = await { start: startDevServer, stop: stopDevServer, restart: restartDevServer }[action](base);
      await mutate(next, { revalidate: false });
      // The ports list is what the preview frame's dropdown reads, and it polls on its own 5s
      // cadence — without this the server is ready but its port is missing for up to five seconds.
      void globalMutate(portsKey(base));
    } catch (err) {
      // `AppError::BadRequest` renders its top-level `error` as the literal "Invalid request" and
      // demotes the real reason to `details`. Reading only `message` turned "no package.json with
      // a dev script was found" into a toast saying nothing at all — so prefer the detail, which
      // is the sentence actually worth showing.
      const detail = err instanceof ApiError ? err.details : null;
      const message = detail || (err instanceof Error ? err.message : `Could not ${action} the dev server.`);
      // A build failure is long and belongs in the log rather than a toast — point at it instead
      // of truncating it into a notification.
      toast.error(message.length > 200 ? `${message.slice(0, 200)}… (open Logs for the rest)` : message);
      setLogsOpen(true);
      void mutate();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="shrink-0 border-b border-line-subtle">
      <div className="flex h-9 items-center gap-2 px-2">
        <span
          aria-hidden
          className={classNames(
            'size-1.5 shrink-0 rounded-full',
            busy || starting ? 'bg-warning' : failed ? 'bg-danger' : running ? 'bg-success' : 'bg-fg-tertiary/50',
          )}
        />
        <span className="shrink-0 text-2xs font-medium text-fg-secondary">
          {isLoading && !status
            ? 'Checking dev server…'
            : busy === 'stop'
              ? 'Stopping…'
              : busy === 'start' || busy === 'restart' || starting
                ? 'Starting…'
                : failed
                  ? 'Dev server failed to start'
                  : running
                    ? 'Dev server running'
                    : 'Dev server not running'}
        </span>
        {running && status?.port && (
          <code className="shrink-0 font-mono text-2xs text-fg-tertiary">localhost:{status.port}</code>
        )}
        {external && (
          // Worth saying: Stop is disabled for it, and that would otherwise look like a bug.
          <span className="shrink-0 text-2xs text-fg-tertiary">· started outside Zooid</span>
        )}

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size={7}
            onClick={() => setLogsOpen((open) => !open)}
            iconLeft={
              <CaretRightIcon size={11} className={classNames('transition-transform', logsOpen && 'rotate-90')} />
            }
          >
            Logs
          </Button>
          {running || starting ? (
            <>
              <Button
                variant="ghost"
                size={7}
                disabled={busy !== null}
                onClick={() => void run('restart')}
                iconLeft={<ArrowsClockwiseIcon size={12} />}
              >
                Restart
              </Button>
              <Button
                variant="ghost"
                size={7}
                disabled={busy !== null || external}
                title={external ? 'This server was started outside Zooid, so it must be stopped there' : undefined}
                onClick={() => void run('stop')}
                iconLeft={<StopIcon size={12} />}
              >
                Stop
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size={7}
              disabled={busy !== null}
              onClick={() => void run('start')}
              iconLeft={busy ? <Spinner size={4} /> : <PlayIcon size={12} weight="fill" />}
            >
              Start
            </Button>
          )}
        </div>
      </div>

      {logsOpen && <DevServerLogs base={base} />}
    </div>
  );
}
